/**
 * Shared helpers for soft background polling (messages unread, conversation refresh).
 * Keeps rate-limited Sharetribe/API failures from flooding the UI or CRA error overlay.
 */

export const DEFAULT_MESSAGES_POLL_MS = 30000;
export const DEFAULT_CONVERSATION_POLL_MS = 45000;
/** Min gap between focus/visibility wakeups (interval ticks always run). */
export const MIN_FOCUS_REFETCH_MS = 10000;
export const RATE_LIMIT_BACKOFF_MS = 60000;
export const RATE_LIMIT_BACKOFF_MAX_MS = 300000;

/** HTTP status from SDK errors or Axios-style errors. */
export const getHttpStatus = error => {
  if (!error) {
    return null;
  }
  return error.status || error.response?.status || null;
};

export const isRateLimitError = error => getHttpStatus(error) === 429;

export const isDocumentVisible = () => {
  if (typeof document === 'undefined') {
    return true;
  }
  return document.visibilityState !== 'hidden';
};

/**
 * Schedule a soft poll: skips when the tab is hidden, catches all promise rejections,
 * and supports visibility/focus wakeups with a minimum gap between runs.
 */
export const startSoftPoll = ({
  intervalMs,
  minGapMs = MIN_FOCUS_REFETCH_MS,
  run,
  runOnStart = true,
  isVisible = isDocumentVisible,
}) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  let lastRunAt = 0;
  let destroyed = false;

  const safeRun = ({ force = false } = {}) => {
    if (destroyed || !isVisible()) {
      return;
    }
    const now = Date.now();
    if (!force && lastRunAt && now - lastRunAt < minGapMs) {
      return;
    }
    lastRunAt = now;
    try {
      const result = run();
      if (result && typeof result.then === 'function') {
        result.catch(() => {
          // Soft failure: never surface as an uncaught promise / CRA overlay.
        });
      }
    } catch (e) {
      // Soft failure: ignore sync throw from poll callbacks.
    }
  };

  if (runOnStart) {
    safeRun({ force: true });
  }

  // Interval ticks must not be suppressed by focus min-gap (otherwise a recent
  // focus wakeup can skip the next scheduled poll and stall the unread badge).
  const intervalId = window.setInterval(() => safeRun({ force: true }), intervalMs);

  const onWake = () => safeRun({ force: false });
  window.addEventListener('focus', onWake);
  document.addEventListener('visibilitychange', onWake);

  return () => {
    destroyed = true;
    window.clearInterval(intervalId);
    window.removeEventListener('focus', onWake);
    document.removeEventListener('visibilitychange', onWake);
  };
};
